import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CustomerFacade } from '../../../+store/customer/customer.facade';
import {
  DrawerType,
  IAdminTokenPayload,
  ICustomerUser,
  IEMRLayout
} from '@care-portals/api-interfaces';
import { CustomerDocumentsModalComponent } from '../../common/customer-documents-modal/customer-documents-modal.component';
import { Subject, takeUntil } from 'rxjs';
import { ChatFacade } from '../../../+store/chat/chat.facade';
import { AddressDisplayComponent } from '../../common/address-display/address-display.component';
import { CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { AgePipe } from '../../../pipes/age.pipe';
import { ConsultationFacade } from '../../../+store/consultation/consultation.facade';
import { DrawerStateFacade } from '../../../+store/drawer-state/drawer-state.facade';
import { AuthFacade } from '../../../+store/auth/auth.facade';
import { AppFacade } from '../../../+store/app/app.facade';
import { DosespotService } from '../../../services/dosespot/dosespot.service';

@Component({
  selector: 'portals-emr-customer-summary',
  templateUrl: './customer-summary.component.html',
  styleUrls: ['./customer-summary.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    NzSpinModule,
    NzButtonModule,
    NzToolTipModule,
    NzTagModule,
    AddressDisplayComponent,
    DatePipe,
    NzDescriptionsModule,
    AgePipe,
    TitleCasePipe,
    CustomerDocumentsModalComponent
  ]
})
export class CustomerSummaryComponent implements OnInit, OnDestroy {
  @Input() customerId: string;
  @Input() orderId: number;
  @Output() verificationChanged = new EventEmitter<void>();

  customer: ICustomerUser;
  isDestroyed$ = new Subject();
  user: IAdminTokenPayload;

  allergies: any[] = [];
  medications: any[] = [];
  conditions: any[] = [];
  selectedLayout: IEMRLayout;

  @ViewChild(CustomerDocumentsModalComponent)
  docsModal: CustomerDocumentsModalComponent;

  constructor(
    private readonly customerFacade: CustomerFacade,
    private readonly chatFacade: ChatFacade,
    private readonly consultationFacade: ConsultationFacade,
    private readonly drawerFacade: DrawerStateFacade,
    private readonly authFacade: AuthFacade,
    private readonly appFacade: AppFacade,
    private readonly dosespotService: DosespotService
  ) {}

  get isInsuranceAvailable() {
    return this.customer?.isInsuranceAvailable;
  }

  get hasDrugCardCoverage() {
    return this.customer?.hasDrugCardCoverage;
  }

  get IDVerificationStatus() {
    return this.customer?.verifications?.govId?.verified;
  }

  get IDVerifiedBy() {
    return this.customer?.verifications?.govId?.verifiedBy;
  }

  get customerLink() {
    return this.customerFacade.getCustomerUrl(this.customerId);
  }

  ngOnInit(): void {
    this.appFacade.selectedLayout$
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe(res => {
        this.selectedLayout = res;
      });

    this.customerFacade.selectedCustomer$
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe(customer => {
        this.customer = customer;
      });
    this.customerFacade.loadCustomer(this.customerId);
    this.consultationFacade
      .getLatestConsultation(this.customerId, 'facesheet')
      .then(res => {
        if (res?.length > 0) {
          const answers = res[0].answers;
          this.allergies = this.getTrueKeys(answers.allergies);
          const medicationsList = answers.medications?.list || [];
          const supplementsList = answers.supplements?.list || [];
          this.medications = [
            ...medicationsList.map(medication => medication.name),
            ...supplementsList.map(supplement => supplement.name)
          ];
          this.conditions = [
            ...this.getTrueKeys(answers.conditions),
            ...this.getTrueKeys(answers.surgeries)
          ];
        }
      });

    this.authFacade.user$.pipe(takeUntil(this.isDestroyed$)).subscribe(res => {
      this.user = res;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.customerId) {
      this.customerId = changes.customerId.currentValue;
      this.customerFacade.loadCustomer(this.customerId);
    }
  }

  getTrueKeys(obj: any) {
    try {
      return Object.entries(obj)
        .filter(([key, val]) => val === true && key !== 'isNone')
        .map(([key]) => key);
    } catch (error) {
      return [];
    }
  }
  ngOnDestroy(): void {
    this.customer = null;
    this.isDestroyed$.next(null);
    this.isDestroyed$.complete();
  }

  openChat() {
    //open set to false false so it does not open in main as well and only opens in drawers
    this.chatFacade.openChat(this.customer, 'inbox', 'drawer');
  }

  updateInsuranceAvailable() {
    this.customerFacade.updateCustomer(this.customerId, {
      isInsuranceAvailable: !this.isInsuranceAvailable
    });
  }

  get expiryDateDisplay() {
    const expiryDate = this.customer?.verifications?.govId?.expiry;
    return expiryDate ? new Date(expiryDate).toLocaleDateString() : null;
  }

  openActivity() {
    this.drawerFacade.openDrawer({
      name: DrawerType.CUSTOMER_ACTIVITY,
      props: { customerId: this.customerId }
    });
  }

  openIdImages(filter?: 'drugCard' | 'govId') {
    this.docsModal?.open(filter);
  }

  openCreateRx() {
    if (
      this.selectedLayout?.dosespotUiUrl &&
      this.customer?.['extras']?.dosespotId
    ) {
      this.dosespotService.openDosespot({
        patientId: this.customer.extras.dosespotId,
        orderId: this.orderId.toString()
      });
      return;
    }

    this.drawerFacade.openDrawer({
      name: DrawerType.CREATE_RX,
      props: {
        customerId: this.customer._id,
        orderId: this.orderId
      }
    });
  }
}
